import { useLanguage } from 'context/AppContext';
import './LoggedOutPage.css';
import './auth0.min.css';
import { useNavigate, useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { LoggedOutReason, useAuthorization } from 'hooks/useAuth';
import { Auth0Error } from 'utils/helpers/auth0.helpers';
import { useIsTierZero } from 'hooks/useSubscriptionTier';
import { getLastUsedUserEmail } from 'hooks/useLastUsedUserEmail';
import { getCachedQuickSwitchOptions, QuickSwitchOption } from 'hooks/useQuickSwitchUsersCache';
import { getLastUsedOrgId, setLastUsedOrgId } from 'hooks/useLastUsedOrgId';
import { normaliseEmail } from 'utils/helpers/usersCache.helpers';
import UserQuickSwitcher from 'components/UserQuickSwitcher/UserQuickSwitcher';
import PinLoginDialog from 'components/PinPad/PinLoginDialog';
import useEnablePinLogin from 'hooks/useEnablePinLogin';
import { usePinOrgId } from 'hooks/usePinOrgId';
import { hasCachedPinKey, isCachedRtExpired } from 'utils/helpers/pinKeyCache.helpers';
import { isPinFeatureOn } from 'utils/helpers/pinFeatureFlag.helpers';
import routes from 'routes';
import PageSpinner from 'components/PageSpinner/PageSpinner';

const LoggedOutPage = () => {
  const lang = useLanguage()[0].LoggedOutPage;
  let { reason = 'login' } = useParams<{ reason: LoggedOutReason }>();
  const [searchParams] = useSearchParams();

  const navigate = useNavigate();
  const isTierZero = useIsTierZero();

  const { login: _login, isLoading, error: _error = history.state?.error, isAuthenticated } = useAuthorization();
  const error = useMemo(() => _error && new Auth0Error(_error), [_error]);

  // direct login links (e.g. for UnitingCare MS 365 apps) pass the org id to auto-login into
  const hasOrgIdParam = searchParams.has('org_id');
  const orgId = searchParams.get('org_id')?.trim();
  const invalidOrgLink = reason == 'login' && hasOrgIdParam && !orgId;

  // navigate home if logged in
  useEffect(() => {
    if (isLoading || error || !isAuthenticated) {
      return;
    }

    navigate(isTierZero ? routes.Home.makePath() : '/');
  }, [isLoading, error, isAuthenticated, navigate, isTierZero]);

  // auto-trigger the org-specific login flow for direct login links
  useEffect(() => {
    if (reason != 'login' || !orgId || isLoading || error || isAuthenticated) return;

    _login(false, orgId);
  }, [orgId, isLoading, error, isAuthenticated, _login, reason]);

  const passwordResetRequired =
    error?.error == 'access_denied' && error.error_description?.toLowerCase().includes('password');
  if (passwordResetRequired) reason = 'password';

  const quickSwitchOptions = useMemo(() => getCachedQuickSwitchOptions(), []);
  const lastKnownEmail = useMemo(() => getLastUsedUserEmail(), []);
  const initialSelection = useMemo(() => {
    const lastOrgId = getLastUsedOrgId();
    const matches = quickSwitchOptions.filter(
      ({ email }) => !!lastKnownEmail && normaliseEmail(email) == normaliseEmail(lastKnownEmail),
    );
    return matches.find(({ orgId }) => orgId == lastOrgId) ?? matches[0] ?? null;
  }, [quickSwitchOptions, lastKnownEmail]);
  const [selected, setSelected] = useState<QuickSwitchOption | null>(initialSelection);
  // the account field was cleared: log in without any remembered user
  const [newUser, setNewUser] = useState(false);

  // with a single known user there's nothing to switch to, but the login button should still hint it
  const current = newUser ? null : selected ?? (quickSwitchOptions.length == 1 ? quickSwitchOptions[0] : null);

  const loginAsNewUser = useCallback(() => _login(!!error, undefined, null), [_login, error]);

  const login = useCallback(
    (emailOverride?: string, orgIdOverride?: string) => {
      _login(!!error, orgIdOverride ?? current?.orgId, emailOverride ?? current?.email ?? lastKnownEmail ?? undefined);
    },
    [_login, error, current, lastKnownEmail],
  );

  // the location setting isn't readable while logged out: a cached, unexpired pin.key means PIN login was set up
  // here, and only an explicit browser opt-out turns it off
  const pinOrgId = usePinOrgId();
  const pinLoginOffForBrowser = useEnablePinLogin()[0] === false;
  const [pinLogin, setPinLogin] = useState<{ email: string; orgId?: string } | null>(null);

  const startLogin = useCallback(
    (option?: QuickSwitchOption) => {
      const target = option ?? current;
      const email = target?.email ?? lastKnownEmail;
      const orgId = target?.orgId ?? pinOrgId;
      const canUsePin =
        isPinFeatureOn() &&
        !!email &&
        !!orgId &&
        !pinLoginOffForBrowser &&
        hasCachedPinKey(orgId, email) &&
        !isCachedRtExpired(orgId, email);

      if (canUsePin) {
        // the PIN hooks resolve the org from the last used one
        setLastUsedOrgId(orgId);
        setPinLogin({ email, orgId });
      } else login(option?.email, option?.orgId);
    },
    [current, lastKnownEmail, pinOrgId, pinLoginOffForBrowser, login],
  );

  // switching to a different user auto-submits; re-selecting the initial user just waits for the login button
  const switchUser = useCallback(
    (option: QuickSwitchOption | null) => {
      setSelected(option);
      setNewUser(!option);
      const isInitial = option?.orgId == initialSelection?.orgId && option?.email == initialSelection?.email;
      if (option && !isInitial) startLogin(option);
    },
    [initialSelection, startLogin],
  );

  const recoverInfo = useMemo(() => {
    const [left, right] = lang.recoverInfo.split('{loginPageLink}');
    return (
      <p className="c43d9da6d c1cbe527d">
        {left}
        <a onClick={() => login()} style={{ cursor: 'pointer' }}>
          {lang.loginPage}
        </a>
        {right}
      </p>
    );
  }, [lang.loginPage, lang.recoverInfo, login]);

  return orgId ? (
    <PageSpinner />
  ) : (
    <div className="container">
      <div className="c0750f404 c76f07b14">
        <main className="c0033b4ee c84b1779f">
          <section className="c3ba8b7cd _prompt-box-outer c8f315919 cc4bb4933">
            <div className="c67994730 cabfc8b62">
              <div className="c22103f1c">
                <header className="c26e4e9ab c630e8e3b">
                  <div title="mk-savi-dev" id="custom-prompt-logo" className="logo"></div>

                  <img
                    className="cd6a837d0 c77e17f16"
                    id="prompt-logo-center"
                    src="/savi-logo-300x100.png"
                    alt="mk-savi-dev"
                  />

                  <h1 className="cf790f87e c69ad2b94">{reason == 'login' ? lang.welcome : lang.loggedOut}</h1>

                  <div className="mb16">
                    <p className="c43d9da6d c1cbe527d">{lang.reason[reason]}</p>
                  </div>
                </header>

                {reason == 'password' && (
                  <div className="cd418d3fa c945c1780">
                    <div>{lang.passwordReset.instructionsTitle}</div>
                    <ol style={{ listStyle: 'auto', textAlign: 'left', marginTop: '8px' }}>
                      {lang.passwordReset.instructions.map((instruction, i) => (
                        <li key={i}>{instruction}</li>
                      ))}
                    </ol>
                  </div>
                )}

                {reason == 'error' && (
                  <div className="cd418d3fa c945c1780">
                    {error?.error_description || error?.message || lang.unknownError}
                  </div>
                )}

                {invalidOrgLink && <div className="cd418d3fa c945c1780">{lang.invalidOrgLink}</div>}

                <div className="cd418d3fa c945c1780">
                  {quickSwitchOptions.length > 1 && (
                    <div className="mb32">
                      <UserQuickSwitcher
                        value={selected}
                        onChange={switchUser}
                        options={quickSwitchOptions}
                        label={lang.switchAccount}
                      />
                    </div>
                  )}
                  <div className="mb32">
                    <button
                      type="submit"
                      name="action"
                      value="default"
                      className="ccfd14389 c00a4c297 c50fd13c0 c04f4358f mt0"
                      style={{ marginTop: '0px' }}
                      data-action-button-primary="true"
                      onClick={() => (reason == 'password' ? login() : newUser ? loginAsNewUser() : startLogin())}
                    >
                      {reason == 'password' ? lang.passwordReset.resetPassword : lang.login}
                    </button>
                  </div>

                  {reason != 'password' && (!!lastKnownEmail || quickSwitchOptions.length > 0) && (
                    <div className="mb32">
                      <p className="c43d9da6d c1cbe527d">
                        <a onClick={loginAsNewUser} style={{ cursor: 'pointer' }}>
                          {lang.differentAccount}
                        </a>
                      </p>
                    </div>
                  )}

                  <div className="mb32">{recoverInfo}</div>

                  <div>
                    <p className="c43d9da6d c1cbe527d">{lang.subscriptionInfo}</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
      </div>
      {pinLogin && (
        <PinLoginDialog
          open
          email={pinLogin.email}
          onClose={() => setPinLogin(null)}
          onUsePassword={() => {
            const { email, orgId } = pinLogin;
            setPinLogin(null);
            login(email, orgId);
          }}
        />
      )}
    </div>
  );
};

export default LoggedOutPage;
